// §4 S13's END STOPPAGE confirm — the office ender of an open §2.7 downtime row
// (§5 e35, `POST /v1/downtime/{id}/end`; D-299 owner ruling H, 2026-09-25).
//
// WHY THIS EXISTS. §3.1's close guard refuses while an op-bound downtime row on
// the order is open. The floor ends a row with `downtime_ended`, but it can only
// compose that from a station whose op-run screen still lists live work, and a
// finished order's station often lists none. Until e35 an open row there had no
// ender on any surface, so a tablet could block a close nobody could unblock.
// This dialog is the ender that is always reachable.
//
// WHAT THE DESIGN COMMITS TO, and what each commitment protects against:
//
//  * A CONFIRM, NOT A ONE-TAP. Ending a stoppage rewrites recorded floor truth
//    (the scoreboard's downtime minutes, the close guard), so it is a stated
//    decision. The note is REQUIRED — the server's own rule (§2.17: a resolution
//    that changes floor truth carries a note), mirrored exactly: required and
//    trimmed, never more.
//  * "RUNNING AGAIN SINCE" IS OPTIONAL AND MEANS WHEN THE MACHINE CAME BACK. The
//    usual case is a forgotten end found hours later; ending it "now" would book
//    every minute since as downtime. Omitted means now. The one bound this
//    client can check without a clock is mirrored — it must be after the stop
//    began (`not_after_start`); "not in the future" is the SERVER's clock and
//    only the server answers it (422 `DOWNTIME_END_AT_INVALID`, bound
//    `in_future`), rendered at the field. So is "no more than 24 hours ago"
//    (bound `before_window`, carrying `earliest_ended_at`; D-299 owner ruling
//    R11 bounds e35 exactly as the floor end's §9.5 clamp).
//  * ONE IDEMPOTENCY KEY PER INTENT. The same body retried (a lost response)
//    replays the first answer under the same key; a changed body is a new
//    intent and mints a fresh one, so it cannot replay a stored refusal.
//  * THE 409 IS NEWS, NOT AN ERROR. `DOWNTIME_ALREADY_ENDED` means somebody —
//    another admin, the floor's own end, an earlier try — already ended it.
//    The dialog says when, offers no retry, and the hook re-reads the open list
//    so the stale row leaves the board.
//
// NO MONEY. A downtime row carries none.

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { formatTimestamp } from "../../../../lib/datetime";
import { newIdempotencyKey, RequestError, requestIdOf } from "../../../../lib/request";
import {
  useEndDowntime,
  type DowntimeLog,
  type DowntimeLogEndResponse,
} from "../hooks/production-queries";
import { productionErrorMessage } from "../lib/format";

/** The contract's own ceiling on `note` (`DowntimeLogEndRequest.note`). */
export const END_NOTE_MAX = 2000;

/**
 * How far past the server's `earliest_ended_at` the `before_window` sentence
 * points the reader (D-299 owner ruling R11, round-3 refutation).
 *
 * The server stamps `earliest_ended_at` as its own now − 24 h AT THE MOMENT IT
 * REFUSES, and that edge walks forward with the clock. Printed as it came, the
 * sentence invited a retry that was refused again: the field takes whole
 * minutes, the formatter shows whole minutes (truncating the seconds), so the
 * minute the reader copies is at or before the old edge, and by the time they
 * press End the edge has moved past it. Ten minutes is the reader's time to
 * re-type and confirm; the instant is then rounded UP to a whole minute so the
 * minute on the glass is the instant that is safe to type.
 */
export const BEFORE_WINDOW_RETRY_MARGIN_MS = 10 * 60_000;

/** The instant the `before_window` sentence names: the server's edge plus the
 * margin, rounded up to a whole minute. `undefined` for an edge that does not
 * parse, which the dialog then words without a time. */
export function retryableEarliestEnd(earliestEndedAt: string | undefined): string | undefined {
  // An absent edge parses as NaN too, so one guard covers both.
  const edgeMs = Date.parse(earliestEndedAt ?? "");
  if (Number.isNaN(edgeMs)) return undefined;
  const minuteMs = 60_000;
  return new Date(
    Math.ceil((edgeMs + BEFORE_WINDOW_RETRY_MARGIN_MS) / minuteMs) * minuteMs,
  ).toISOString();
}

/** A refusal the dialog renders AT a control rather than as a generic error. */
type Refusal =
  | { readonly kind: "already_ended"; readonly endedAt: string | undefined }
  | {
      readonly kind: "end_at";
      readonly bound: string | undefined;
      /** Only with `bound: before_window` (D-299 owner ruling R11). */
      readonly earliestEndedAt: string | undefined;
    }
  | { readonly kind: "not_found" }
  | { readonly kind: "other"; readonly error: unknown };

/** Read a top-level problem extension off a thrown `RequestError`. The
 * generated `Problem` type names only the RFC 9457 core, so the extensions
 * e35 documents (`ended_at`, `bound`) are read structurally. */
function extensionOf(error: unknown, key: string): string | undefined {
  if (!(error instanceof RequestError) || error.problem === undefined) return undefined;
  const value = (error.problem as Record<string, unknown>)[key];
  return typeof value === "string" ? value : undefined;
}

export function classifyEndRefusal(error: unknown): Refusal {
  if (error instanceof RequestError) {
    if (error.code === "DOWNTIME_ALREADY_ENDED") {
      return { kind: "already_ended", endedAt: extensionOf(error, "ended_at") };
    }
    if (error.code === "DOWNTIME_END_AT_INVALID") {
      return {
        kind: "end_at",
        bound: extensionOf(error, "bound"),
        earliestEndedAt: extensionOf(error, "earliest_ended_at"),
      };
    }
    if (error.code === "DOWNTIME_LOG_NOT_FOUND") return { kind: "not_found" };
  }
  return { kind: "other", error };
}

export function EndStoppageDialog({
  log,
  reasonLabel,
  onClose,
  onEnded,
}: {
  /** The open row being ended — its `started_at` bounds the stated end. */
  readonly log: DowntimeLog;
  /** The reason in the reader's words, so the confirm names WHICH stop. */
  readonly reasonLabel: string;
  readonly onClose: () => void;
  readonly onEnded: (result: DowntimeLogEndResponse) => void;
}) {
  const { t, i18n } = useTranslation();
  const end = useEndDowntime();
  const [note, setNote] = useState("");
  const [endedAtLocal, setEndedAtLocal] = useState("");
  const [refusal, setRefusal] = useState<Refusal | undefined>(undefined);
  /** The key rides the INTENT: same body → same key (a retry replays). */
  const intent = useRef<{ fingerprint: string; key: string } | undefined>(undefined);

  const trimmed = note.trim();
  const statedMs = endedAtLocal.length > 0 ? new Date(endedAtLocal).getTime() : undefined;
  const startedMs = Date.parse(log.started_at);
  // The client-checkable half of e35's bounds (see the head note).
  const endAtUnreadable = statedMs !== undefined && Number.isNaN(statedMs);
  const endAtNotAfterStart =
    statedMs !== undefined && !Number.isNaN(statedMs) && statedMs <= startedMs;

  const blockedReason =
    trimmed.length === 0
      ? t("production.blocked.end.noteRequired")
      : trimmed.length > END_NOTE_MAX
        ? t("production.blocked.end.noteTooLong", { max: END_NOTE_MAX })
        : endAtUnreadable
          ? t("production.blocked.end.endAtUnparseable")
          : endAtNotAfterStart
            ? t("production.blocked.end.endAtNotAfterStart", {
                time: formatTimestamp(log.started_at, i18n.language),
              })
            : undefined;

  const alreadyEnded = refusal?.kind === "already_ended" ? refusal : undefined;

  const submit = (): void => {
    if (blockedReason !== undefined) return;
    const endedAt =
      statedMs !== undefined && !Number.isNaN(statedMs)
        ? new Date(statedMs).toISOString()
        : undefined;
    const fingerprint = JSON.stringify({ note: trimmed, endedAt: endedAt ?? null });
    const key =
      intent.current?.fingerprint === fingerprint ? intent.current.key : newIdempotencyKey();
    intent.current = { fingerprint, key };
    setRefusal(undefined);
    end.mutate(
      { id: log.id, note: trimmed, endedAt, idempotencyKey: key },
      {
        onSuccess: (result) => {
          onEnded(result);
        },
        onError: (error) => {
          setRefusal(classifyEndRefusal(error));
        },
      },
    );
  };

  const retryAfter =
    refusal?.kind === "end_at" && refusal.bound === "before_window"
      ? retryableEarliestEnd(refusal.earliestEndedAt)
      : undefined;
  const endAtHelper =
    refusal?.kind === "end_at"
      ? refusal.bound === "in_future"
        ? t("production.blocked.end.endAtInFuture")
        : refusal.bound === "not_after_start"
          ? t("production.blocked.end.endAtNotAfterStart", {
              time: formatTimestamp(log.started_at, i18n.language),
            })
          : refusal.bound === "before_window"
            ? // D-299 owner ruling R11: e35 is bounded exactly as the floor's
              // end, never more than 24 h back. Without this arm the refusal
              // fell through to "could not be read", which sends the reader to
              // re-pick a time the server read perfectly well. The edge is
              // the SERVER's clock, read off the problem and never computed
              // here; the time named is that edge plus the retry margin and
              // worded "after" (see `retryableEarliestEnd`), so the minute on
              // the glass is one the server will still take when End is
              // pressed again.
              retryAfter !== undefined
              ? t("production.blocked.end.endAtBeforeWindow", {
                  time: formatTimestamp(retryAfter, i18n.language),
                })
              : t("production.blocked.end.endAtBeforeWindowNoTime")
            : t("production.blocked.end.endAtUnparseable")
      : undefined;

  return (
    <Modal
      label={t("production.blocked.end.title")}
      heading={t("production.blocked.end.title")}
      closeLabel={t("production.common.close")}
      onClose={onClose}
      className="production-modal"
      testId="blocked-end-dialog"
      pending={end.isPending}
    >
      <p className="production-hint" data-testid="blocked-end-subject">
        {t("production.blocked.end.subject", {
          reason: reasonLabel,
          time: formatTimestamp(log.started_at, i18n.language),
        })}
      </p>

      {alreadyEnded !== undefined ? (
        // The 409: somebody already ended it. Said with WHEN, no retry — the
        // row leaves the board on the re-read the hook started.
        <p className="production-note" role="status" data-testid="blocked-end-already">
          {alreadyEnded.endedAt !== undefined
            ? t("production.blocked.end.alreadyEnded", {
                time: formatTimestamp(alreadyEnded.endedAt, i18n.language),
              })
            : t("production.blocked.end.alreadyEndedNoTime")}
        </p>
      ) : (
        <form
          className="blocked-end-form"
          data-testid="blocked-end-form"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <Field label={t("production.blocked.end.noteLabel")} wide>
            <textarea
              value={note}
              data-testid="blocked-end-note"
              required
              maxLength={END_NOTE_MAX}
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
          </Field>
          <Field label={t("production.blocked.end.endedAtLabel")} wide>
            <input
              type="datetime-local"
              value={endedAtLocal}
              data-testid="blocked-end-ended-at"
              onChange={(event) => {
                setEndedAtLocal(event.target.value);
                // The server's bound answered the PREVIOUS time.
                if (refusal?.kind === "end_at") setRefusal(undefined);
              }}
            />
          </Field>
          <p className="production-hint">{t("production.blocked.end.endedAtHint")}</p>
          {endAtHelper !== undefined ? (
            <p className="form-error" role="alert" data-testid="blocked-end-ended-at-error">
              {endAtHelper}
            </p>
          ) : null}
          {refusal?.kind === "not_found" ? (
            <p className="form-error" role="alert" data-testid="blocked-end-not-found">
              {t("production.blocked.end.notFound")}
            </p>
          ) : null}
          {refusal?.kind === "other" ? (
            <p className="form-error" role="alert" data-testid="blocked-end-error">
              {productionErrorMessage(t, refusal.error, "production.blocked.end.error")}
              {requestIdOf(refusal.error) !== undefined ? (
                <>
                  {" "}
                  {t("production.common.requestId")}:{" "}
                  <code className="ref-id">{requestIdOf(refusal.error)}</code>
                </>
              ) : null}
            </p>
          ) : null}
          <div className="form-actions">
            {/* type="submit" explicitly — Button defaults to type="button". */}
            <Button
              type="submit"
              variant="primary"
              data-testid="blocked-end-confirm"
              disabled={blockedReason !== undefined}
              busy={end.isPending}
              busyLabel={t("production.blocked.end.ending")}
            >
              {t("production.blocked.end.confirm")}
            </Button>
            <Button data-testid="blocked-end-cancel" disabled={end.isPending} onClick={onClose}>
              {t("production.common.cancel")}
            </Button>
          </div>
          {/* D-118 — a dead Confirm says why, at the control. */}
          {blockedReason !== undefined ? (
            <p className="production-hint" role="status" data-testid="blocked-end-blocked">
              {blockedReason}
            </p>
          ) : null}
        </form>
      )}

      {alreadyEnded !== undefined ? (
        <div className="form-actions">
          <Button variant="primary" data-testid="blocked-end-dismiss" onClick={onClose}>
            {t("production.common.close")}
          </Button>
        </div>
      ) : null}
    </Modal>
  );
}
